import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import clsx from "clsx";
import { Routes } from "../../../root/routes";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import {
  AlertCard,
  Breadcrumbs,
  Button,
  Modal,
  Pill,
  SimpleCheckboxControlled,
  useSnackBar,
} from "@truevault/aquilo";
import { DATA_FLOWS_CONTENT_MAX_WIDTH, DataFlowsStickyHeader } from "./DataFlowsPageHeader";
import {
  useInvalidateOnboardingProgress,
  useOnboardingProgress,
} from "../../../../common/hooks/useOnboardingProgress";
import { useHistory } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../../hooks";
import { useOrganizationVendors, useOrgDataSources } from "../../../hooks/useOrganizationVendors";
import { useDataSourceCatalog } from "../../../../common/hooks/useVendors";
import { OrganizationVendor } from "../../../../common/models";
import { Api } from "../../../../common/service/Api";
import {
  AddSourceModal,
  isOtherDataSource,
  LaunchAddSourcesModalConfig,
  OTHER_SOURCE_EXCLUDED_CATEGORIES,
  OTHER_SOURCE_EXCLUDED_VENDOR_KEYS,
  SelectedSource,
} from "./AddSourceModal";
import { useDataMap } from "../../../../common/hooks/api/useDataMap";
import {
  SOURCE_KEY_CONSUMER_DIRECTLY,
  SOURCE_KEY_OTHER_INDIVIDUALS,
  VENDOR_CATEGORY_AD_NETWORK,
  VENDOR_CATEGORY_DATA_ANALYTICS_PROVIDER,
  VENDOR_CATEGORY_DATA_BROKER,
  VENDOR_CATEGORY_RETAIL_PARTNER,
} from "../../../types/Vendor";
import {
  collectedPICsFromFilteredCollection,
  receivedPICsFromFilteredReceived,
} from "../../../../common/service/resources/data-map/dataMapTransformers";
import { useFilteredPicGroups } from "../../../components/organisms/collection-groups/ConsumerDataCollectionCheckboxes";
import { DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import {
  useReceivedForCollectionGroups,
  UseReceivedForCollectionGroups,
} from "../../../hooks/useReceivedForCollectionGroups";
import { Loading } from "../../../components";
import {
  useConsumerCollectionGroup,
  useNonEmploymentCollectionGroups,
} from "../../../hooks/useCollectionGroups";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
  OrganizationDataSourceDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { FingerPrintIcon, PlusIcon, UserGroupIcon, UserIcon } from "@heroicons/react/24/outline";
import { DataMapOperation } from "../../../../common/service/server/dto/datamap/DataMapRequestDto";
import { SourcesExplanatoryCard } from "./DataFlowsExplanatoryCards";
import { SourceRecipientPrompt, SourceRecipientPromptModal } from "./SourceRecipientPromptModal";
import { shouldShowInInventoryContext } from "../vendor-inventory/vendorInventoryUtils";
import { addRecipientContextSettings } from "../../../util/vendors";
import { NavCountBadge } from "./DataFlowsDisclosuresPage";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import {
  CONSUMER_DIRECTLY_QUESTION,
  CONSUMER_DIRECTLY_SLUG,
  OTHER_CONSUMERS_QUESTION,
  OTHER_CONSUMERS_SLUG,
} from "../../../surveys/dataMapSourcesSurvey";
import { CONSUMER_SOURCES_SURVEY_NAME } from "../../get-compliant/data-map/ConsumerDataSourcesPage";
import {
  CONSUMER_INCENTIVES_SURVEY_NAME,
  OFFERS_CONSUMER_INCENTIVES_SLUG,
} from "../../../surveys/consumerCollectionIncentivesSurvey";
import { ProgressFooter, ProgressFooterActions } from "../../get-compliant/ProgressFooter";
import { isEmptyDataMap } from "../../../../common/service/resources/data-map/dataMapHelpers";
import { useDataFlowsOnboardingFooterGate } from "./dataFlowsCompletion";
// Render-time-only cycle with the dashboard module, same shape as dataFlowsCompletion's import
// from this file.
import {
  CONSUMER_DATA_FLOWS_DASHBOARD_CONFIG,
  useDataFlowCollectionGroupsCompletion,
} from "./DataFlowsDashboardPage";

interface VendorSourcingCardProps {
  org: OrganizationDto;
  source: OrganizationDataSourceDto;
  collectionGroupId: CollectionGroupDetailsDto["id"] | CollectionGroupDetailsDto["id"][];
  dataMap: DataMapDto;
  receivedData: UseReceivedForCollectionGroups;
  variant?: "sources-page" | "modal";
  /** Removes this org data source for the collection context used on the sources page. */
  onRemoveSource?: () => Promise<void>;
  /** Used for built-in sourcing: persist "not a source" (e.g. survey + refresh). */
  onResetLocalChoice?: () => void | Promise<void>;
  sourceName?: string;
  /** One-click select of every collected category (consumer primary source only, crit c_86fc7c). */
  showSelectAll?: boolean;
}

function useVendorSourceActionState() {
  const [isRunning, setIsRunning] = useState(false);

  const runAction = useCallback(async (action: () => void | Promise<void>) => {
    setIsRunning(true);
    try {
      await Promise.resolve(action());
    } finally {
      setIsRunning(false);
    }
  }, []);

  return { isRunning, runAction };
}

/**
 * Configure which PICs are sourced from the vendor for the collection group.
 */
export function VendorSourcingCard({
  org,
  source,
  collectionGroupId,
  dataMap,
  receivedData,
  variant = "sources-page",
  onRemoveSource,
  onResetLocalChoice,
  sourceName,
  showSelectAll = false,
}: VendorSourcingCardProps) {
  const { isRunning: isRemoving, runAction: runRemoveSource } = useVendorSourceActionState();
  const { isRunning: isResettingChoice, runAction: runResetLocalChoice } =
    useVendorSourceActionState();
  const filteredPicGroups = useFilteredPicGroups({ dataMap, org });

  const selectedPicIds = useMemo(() => {
    return receivedPICsFromFilteredReceived(dataMap, {
      collectionGroupId: collectionGroupId,
      vendorId: source.vendorId,
    }).map((pic) => pic.id);
  }, [dataMap, collectionGroupId, source.vendorId]);

  const [expandedByPicGroupId, setExpandedByPicGroupId] = useState<Record<string, boolean> | null>(
    null,
  );
  const [updatingByPicGroupId, setUpdatingByPicGroupId] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isEmptyDataMap(dataMap) || expandedByPicGroupId !== null) {
      return;
    }

    const initialSelectedPicIds = receivedPICsFromFilteredReceived(dataMap, {
      collectionGroupId: collectionGroupId,
      vendorId: source.vendorId,
    }).map((pic) => pic.id);
    const initial: Record<string, boolean> = {};

    for (const picId of initialSelectedPicIds) {
      const picGroupId = dataMap.categories[picId]?.picGroupId;
      if (picGroupId) {
        initial[picGroupId] = true;
      }
    }

    setExpandedByPicGroupId(initial);
    setIsCardCollapsed(initialSelectedPicIds.length > 0);
  }, [dataMap, expandedByPicGroupId, collectionGroupId, source.vendorId]);

  // Collapsed cards show just the sourced categories (crit c_1113f5). A card with nothing
  // selected yet opens expanded, since it still needs configuring. Seeded alongside
  // expandedByPicGroupId rather than in a useState initializer: on the first render the data map
  // is usually still empty, so an initializer would call every card unselected and open them all.
  const [isCardCollapsed, setIsCardCollapsed] = useState<boolean | null>(null);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);

  if (expandedByPicGroupId === null || isCardCollapsed === null) {
    return null;
  }

  const isModal = variant === "modal";
  const collapsible = !isModal;
  const showCollapsed = collapsible && isCardCollapsed;

  const collectedPicsByGroupId: Record<string, { id: string; name: string }[]> = {};
  for (const picGroup of filteredPicGroups) {
    collectedPicsByGroupId[picGroup.id] = collectedPICsFromFilteredCollection(dataMap, {
      collectionGroupId,
      picGroupId: picGroup.id,
    });
  }
  const sourcedGroupNames = filteredPicGroups
    .filter((picGroup) =>
      collectedPicsByGroupId[picGroup.id].some((pic) => selectedPicIds.includes(pic.id)),
    )
    .map((picGroup) => picGroup.name);
  const allCollectedPicIds = filteredPicGroups.flatMap((picGroup) =>
    collectedPicsByGroupId[picGroup.id].map((pic) => pic.id),
  );
  const allSelected =
    allCollectedPicIds.length > 0 &&
    allCollectedPicIds.every((picId) => selectedPicIds.includes(picId));

  return (
    <div
      className={
        isModal
          ? "rounded-[10px] border border-neutral-300 bg-white p-[24px]"
          : "p-[24px] w-full border border-solid border-neutral-300 rounded-[10px] bg-blue-50"
      }
      onClick={showCollapsed ? () => setIsCardCollapsed(false) : undefined}
    >
      {!isModal && (
        <div
          className="flex items-center justify-between gap-[12px] cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setIsCardCollapsed(!isCardCollapsed);
          }}
        >
          <div className="flex items-center min-w-0 gap-[10px]">
            <SourceAvatar source={source} />
            <div className="text-[18px] font-medium leading-[28px]">
              {sourceName ?? source.name} is sourcing:
            </div>
            {selectedPicIds.length === 0 && (
              <span className="flex-shrink-0">
                <Pill variant="warning" noMargin>
                  Incomplete
                </Pill>
              </span>
            )}
          </div>
          <Button
            color="link"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              setIsCardCollapsed(!isCardCollapsed);
            }}
          >
            {showCollapsed ? "Edit" : "Done"}
          </Button>
        </div>
      )}
      {collapsible && (
        <div
          aria-hidden={!showCollapsed}
          style={{
            display: "grid",
            gridTemplateRows: showCollapsed ? "1fr" : "0fr",
            transition: "grid-template-rows 250ms ease",
          }}
        >
          <div
            style={{
              overflow: "hidden",
              minHeight: 0,
              visibility: showCollapsed ? "visible" : "hidden",
              transition: "visibility 250ms",
            }}
          >
            <div className="flex flex-wrap items-center gap-[6px] mt-[12px]">
              {sourcedGroupNames.length > 0 ? (
                sourcedGroupNames.map((name) => (
                  <span
                    key={name}
                    className="text-[12px] font-medium"
                    style={{
                      padding: "2px 10px",
                      borderRadius: 999,
                      backgroundColor: "#fff",
                      border: "1px solid #E8E5EE",
                      color: "#534E65",
                    }}
                  >
                    {name}
                  </span>
                ))
              ) : (
                <span className="text-[13px] text-neutral-500">No categories selected yet</span>
              )}
            </div>
          </div>
        </div>
      )}
      <div
        aria-hidden={showCollapsed}
        style={
          collapsible
            ? {
                display: "grid",
                gridTemplateRows: showCollapsed ? "0fr" : "1fr",
                transition: "grid-template-rows 250ms ease",
              }
            : undefined
        }
      >
        <div
          style={
            collapsible
              ? {
                  overflow: "hidden",
                  minHeight: 0,
                  visibility: showCollapsed ? "hidden" : "visible",
                  transition: "visibility 250ms",
                }
              : undefined
          }
        >
          {showSelectAll && !isModal && (
            <div className="mt-[12px] inline-flex items-center relative">
              {isSelectingAll && (
                <div className="absolute inset-0 z-10 rounded-[6px] bg-white bg-opacity-50" />
              )}
              <SimpleCheckboxControlled
                containerClassName="items-center gap-[6px]"
                name={`${source.vendorId}-select-all-sourced`}
                checked={allSelected}
                disabled={isSelectingAll}
                onChange={async (newChecked) => {
                  setIsSelectingAll(true);
                  try {
                    await receivedData.actions.updateReceived({
                      vendorId: source.vendorId,
                      receivedIds: allCollectedPicIds,
                      received: newChecked,
                    });
                    setExpandedByPicGroupId(
                      Object.fromEntries(filteredPicGroups.map((g) => [g.id, newChecked])),
                    );
                  } finally {
                    setIsSelectingAll(false);
                  }
                }}
                label={
                  <span className="text-[13px] font-medium text-primary-800 whitespace-nowrap">
                    Select all collected categories
                  </span>
                }
              />
            </div>
          )}
          {/* 12px between the header and the category boxes (crit c_925378). The modal has no
              header, and lists plain rows like its Data Disclosures tab instead of boxes in a box. */}
          <div className={isModal ? "space-y-[12px]" : "space-y-[12px] mt-[12px]"}>
            {filteredPicGroups.map((picGroup) => {
              const hasSensitivePics = picGroup.categories.some(
                (cat) => cat.sensitiveRegions?.length > 0,
              );
              const collectedPicsInGroup = collectedPICsFromFilteredCollection(dataMap, {
                collectionGroupId,
                picGroupId: picGroup.id,
              });

              if (collectedPicsInGroup.length === 0) {
                return null;
              }

              const expandPicGroupCheckboxId = `${source.vendorId}-${picGroup.id}-expand-pics`;
              const isUpdatingPicGroup = updatingByPicGroupId[picGroup.id] === true;

              return (
                <div key={`${picGroup.key}-section`}>
                  <div
                    className={
                      isModal
                        ? "relative rounded-[6px] px-[10px] py-[6px] w-full"
                        : "relative border border-solid border-neutral-300 rounded-[4px] p-[12px] w-full bg-white"
                    }
                  >
                    {isUpdatingPicGroup && (
                      <div className="absolute inset-0 z-10 rounded-[4px] bg-white bg-opacity-50">
                        <div className="flex h-full w-full items-center justify-center">
                          <CircularProgress size="2em" />
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between items-start gap-[12px]">
                      <SimpleCheckboxControlled
                        name={expandPicGroupCheckboxId}
                        label={picGroup.name}
                        checked={expandedByPicGroupId[picGroup.id] === true}
                        disabled={isUpdatingPicGroup}
                        onChange={async (checked) => {
                          if (checked) {
                            setExpandedByPicGroupId((prev) => ({ ...prev, [picGroup.id]: true }));

                            // Checking a category selects everything collected in it (POL-7889 did
                            // this for single-item categories); the user unticks what doesn't apply.
                            setUpdatingByPicGroupId((prev) => ({
                              ...prev,
                              [picGroup.id]: true,
                            }));
                            try {
                              await receivedData.actions.updateReceived({
                                vendorId: source.vendorId,
                                receivedIds: collectedPicsInGroup.map((pic) => pic.id),
                                received: true,
                              });
                            } finally {
                              setUpdatingByPicGroupId((prev) => ({
                                ...prev,
                                [picGroup.id]: false,
                              }));
                            }

                            return;
                          }

                          setUpdatingByPicGroupId((prev) => ({ ...prev, [picGroup.id]: true }));
                          try {
                            await receivedData.actions.updateReceived({
                              vendorId: source.vendorId,
                              receivedIds: collectedPicsInGroup.map((pic) => pic.id),
                              received: false,
                            });
                            setExpandedByPicGroupId((prev) => ({ ...prev, [picGroup.id]: false }));
                          } finally {
                            setUpdatingByPicGroupId((prev) => ({
                              ...prev,
                              [picGroup.id]: false,
                            }));
                          }
                        }}
                      />
                      {hasSensitivePics && (
                        <div className="ml-auto flex shrink-0 items-center">
                          <FingerPrintIcon className="w-[25px] h-[25px] text-primary-700" />
                          <div className="text-[12px] font-medium text-primary-800 ml-[4px]">
                            Sensitive Data
                          </div>
                        </div>
                      )}
                    </div>
                    {/* align with label text (16px checkbox + 12px gap in SimpleCheckboxControlled) */}
                    <div className="pl-[28px] text-[14px] leading-[20px] text-neutral-500">
                      You collect: {collectedPicsInGroup.map((pic) => pic.name).join(", ")}
                    </div>
                    {expandedByPicGroupId[picGroup.id] && (
                      <div className="pl-[32px] grid grid-cols-2 gap-[12px]">
                        {collectedPicsInGroup.map((pic) => {
                          return (
                            <div key={`${source.vendorId}-${pic.id}-checkbox`}>
                              <SimpleCheckboxControlled
                                name={`${source.vendorId}-${pic.id}-checkbox`}
                                label={pic.name}
                                checked={selectedPicIds.includes(pic.id)}
                                disabled={isUpdatingPicGroup}
                                onChange={async (newChecked) => {
                                  await receivedData.actions.updateReceived({
                                    vendorId: source.vendorId,
                                    receivedIds: [pic.id],
                                    received: newChecked,
                                  });
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!isModal && (onRemoveSource != null || onResetLocalChoice != null) && (
            <div className="mt-[20px] pt-[16px] flex justify-center">
              {onRemoveSource != null && (
                <button
                  type="button"
                  className="text-center text-[14px] font-medium text-primary-600 underline cursor-pointer bg-transparent border-0 p-0 disabled:cursor-not-allowed disabled:text-neutral-400"
                  disabled={isRemoving}
                  onClick={() => setIsConfirmRemoveOpen(true)}
                >
                  {isRemoving ? "Removing…" : "Remove as Source"}
                </button>
              )}
              {onResetLocalChoice != null && (
                <button
                  type="button"
                  className="text-center text-[14px] font-medium text-primary-600 underline cursor-pointer bg-transparent border-0 p-0 disabled:cursor-not-allowed disabled:text-neutral-400"
                  disabled={isResettingChoice}
                  onClick={() => runResetLocalChoice(onResetLocalChoice)}
                >
                  {isResettingChoice ? "Updating…" : "Not a Source"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {isConfirmRemoveOpen && onRemoveSource != null && (
        <RemoveSourceConfirmationModal
          sourceName={sourceName ?? source.name}
          onConfirm={() => {
            setIsConfirmRemoveOpen(false);
            runRemoveSource(onRemoveSource);
          }}
          onCancel={() => setIsConfirmRemoveOpen(false)}
        />
      )}
    </div>
  );
}

/** Consumer and Referrals get an icon; vendors show their logo, or their initial without one. */
function SourceAvatar({ source }: { source: OrganizationDataSourceDto }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const Icon =
    source.vendorKey === SOURCE_KEY_CONSUMER_DIRECTLY
      ? UserIcon
      : source.vendorKey === SOURCE_KEY_OTHER_INDIVIDUALS
      ? UserGroupIcon
      : null;

  if (Icon) {
    return (
      <span
        className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[6px]"
        style={{ backgroundColor: "#F3F0FE" }}
      >
        <Icon className="h-[16px] w-[16px]" style={{ color: "#3C1DA5" }} />
      </span>
    );
  }
  if (source.logoUrl && !logoFailed) {
    return (
      <img
        src={source.logoUrl}
        alt=""
        className="h-[24px] w-[24px] shrink-0 rounded-[4px] object-contain"
        onError={() => setLogoFailed(true)}
      />
    );
  }
  return (
    <span
      className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[6px] text-[12px] font-medium"
      style={{ backgroundColor: "#EEEDFE", color: "#3C3489" }}
    >
      {source.name.charAt(0).toUpperCase()}
    </span>
  );
}

/** "Are you sure?" gate before a vendor is removed as a source; the removal itself is undoable via the toast. */
export function RemoveSourceConfirmationModal({
  sourceName,
  onConfirm,
  onCancel,
}: {
  sourceName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      title={`Remove ${sourceName} as a source?`}
      body={`${sourceName} will no longer be listed as a source of personal data for your business, and the categories you marked as sourced from it will be cleared.`}
      alignText="left"
      alignButton="right"
      firstButton="Cancel"
      onFirstButtonClick={onCancel}
      secondButton="Yes, I'm sure"
      secondButtonColor="warning"
      onSecondButtonClick={onConfirm}
      showOverlay
      onClose={onCancel}
    />
  );
}

export function isVendorDataSourcingComplete(
  dataMap: DataMapDto,
  collectionGroupId: string | string[],
  vendorId: string,
): boolean {
  if (isEmptyDataMap(dataMap)) {
    return false;
  }

  return (
    receivedPICsFromFilteredReceived(dataMap, {
      collectionGroupId,
      vendorId,
    }).length > 0
  );
}

export function isSourcesPageComplete(
  orgDataSources: OrganizationDataSourceDto[],
  dataMap: DataMapDto,
  collectionGroupId: string | string[],
  collectionContext: CollectionContext = "CONSUMER",
  sourceNameByVendorKey: Record<string, string> = {},
): [boolean, string | null] {
  const contextualSources = (orgDataSources || []).filter((s) =>
    s.collectionContext.includes(collectionContext),
  );

  // require at least one source
  if (contextualSources.length == 0) {
    return [false, "Add at least one source"];
  }

  // for each source, require at least one PIC selected
  const sourcesWithNoSelectedPics = contextualSources.filter((source) => {
    let selectedPicIds = receivedPICsFromFilteredReceived(dataMap, {
      collectionGroupId,
      vendorId: source.vendorId,
    });
    return selectedPicIds.length == 0;
  });

  if (sourcesWithNoSelectedPics.length > 0) {
    return [
      false,
      "Select at least one data category for: " +
        sourcesWithNoSelectedPics
          .map((source) =>
            source.vendorKey ? sourceNameByVendorKey[source.vendorKey] ?? source.name : source.name,
          )
          .join(", "),
    ];
  }

  return [true, null];
}

function CollectionGroupSources({
  org,
  dataMap,
  collectionGroupIds,
  source,
  receivedData,
  onAddAsSource,
  onMarkNotASource,
  surveyAnswer,
  sourceName,
  sectionTitle,
  sectionDescription,
  recommendation,
  showSelectAll = false,
}: {
  org: OrganizationDto;
  dataMap: DataMapDto;
  collectionGroupIds: string[];
  source: OrganizationDataSourceDto | undefined;
  receivedData: UseReceivedForCollectionGroups;
  onAddAsSource: () => Promise<void>;
  onMarkNotASource: () => Promise<void>;
  surveyAnswer: string | undefined;
  sourceName: string;
  sectionTitle: string;
  sectionDescription: string;
  recommendation?: string;
  showSelectAll?: boolean;
}) {
  const [isConsumerSource, setIsConsumerSource] = useState<boolean | null>(null);
  const [isAddingConsumerSource, setIsAddingConsumerSource] = useState(false);
  const [isMarkingConsumerNotSource, setIsMarkingConsumerNotSource] = useState(false);

  const isConsumerChoicePending = isAddingConsumerSource || isMarkingConsumerNotSource;

  useEffect(() => {
    if (surveyAnswer === "true") {
      setIsConsumerSource(true);
    } else if (surveyAnswer === "false") {
      setIsConsumerSource(false);
    }
  }, [surveyAnswer]);

  const runAddConsumerAsSource = async () => {
    setIsAddingConsumerSource(true);
    try {
      await onAddAsSource();
      setIsConsumerSource(true);
    } catch {
      // Survey patch mutation shows its own error snackbar on failure.
    } finally {
      setIsAddingConsumerSource(false);
    }
  };

  return (
    <div className="relative rounded-[8px] border border-solid border-neutral-300">
      {isAddingConsumerSource && (
        <div className="absolute inset-0 z-10 rounded-[8px] bg-white bg-opacity-50">
          <div className="flex h-full w-full items-center justify-center">
            <CircularProgress size="3em" />
          </div>
        </div>
      )}
      <div className="px-[26px] py-[16px]">
        <div className="text-[18px] leading-[20px] text-neutral-900 font-semibold">
          {sectionTitle}
        </div>
        <div className="text-[16px] leading-[24px] text-neutral-600">{sectionDescription}</div>
        {recommendation && (
          <div>
            <AlertCard variant="recommendation" description={recommendation} />
          </div>
        )}
        {!isAddingConsumerSource && isConsumerSource === null && (
          <div className="flex w-full max-w-[530px] justify-between items-center gap-[12px] p-[16px] rounded-[6px] border border-solid border-neutral-300">
            <div className="min-w-0 flex-1">{sourceName}</div>
            <div className="flex shrink-0 gap-[12px]">
              <Button
                color="tertiary"
                size="xs"
                disabled={isConsumerChoicePending}
                onClick={runAddConsumerAsSource}
              >
                Add as a Source
              </Button>
              <Button
                color="tertiary"
                size="xs"
                disabled={isConsumerChoicePending}
                onClick={async () => {
                  setIsMarkingConsumerNotSource(true);
                  try {
                    await onMarkNotASource();
                    setIsConsumerSource(false);
                  } catch {
                    // Survey patch mutation shows its own error snackbar on failure.
                  } finally {
                    setIsMarkingConsumerNotSource(false);
                  }
                }}
              >
                {isMarkingConsumerNotSource ? "Updating…" : "Not a Source"}
              </Button>
            </div>
          </div>
        )}
        {!isAddingConsumerSource && isConsumerSource === false && (
          <div className="flex w-full max-w-[530px] justify-between items-center gap-[12px] p-[16px] rounded-[6px] border border-solid border-neutral-300">
            <div className="min-w-0 flex-1">
              <div>{sourceName}</div>
              <div className="text-[14px] leading-[20px] text-neutral-600 mt-[4px]">
                Marked as <span className="font-medium">not a source</span>. Add this source if that
                changes.
              </div>
            </div>
            <div className="flex shrink-0 gap-[12px]">
              <Button
                color="tertiary"
                size="xs"
                disabled={isConsumerChoicePending}
                onClick={runAddConsumerAsSource}
              >
                Add as a Source
              </Button>
            </div>
          </div>
        )}
        {isConsumerSource === true &&
          !isAddingConsumerSource &&
          collectionGroupIds.length > 0 &&
          source && (
            <div>
              <VendorSourcingCard
                org={org}
                dataMap={dataMap}
                source={source}
                sourceName={sourceName}
                collectionGroupId={collectionGroupIds}
                receivedData={receivedData}
                showSelectAll={showSelectAll}
                onResetLocalChoice={async () => {
                  await onMarkNotASource();
                  setIsConsumerSource(false);
                }}
              />
            </div>
          )}
      </div>
    </div>
  );
}

// Laid out like the vendor category sections below it: an add button, no "Not a Source" answer.
function ReferralsSource({
  org,
  dataMap,
  consumerCollectionGroup,
  consumerSource,
  receivedData,
  offersFinancialIncentives,
  onAddConsumerAsSource,
  onMarkConsumerNotASource,
}: {
  org: OrganizationDto;
  dataMap: DataMapDto;
  consumerCollectionGroup: CollectionGroupDetailsDto;
  consumerSource: OrganizationDataSourceDto | undefined;
  receivedData: UseReceivedForCollectionGroups;
  /** The Business Survey's financial incentives answer; rewards programs often pay for referrals. */
  offersFinancialIncentives: boolean;
  onAddConsumerAsSource: () => Promise<void>;
  onMarkConsumerNotASource: () => Promise<void>;
}) {
  const [isAddingReferralsSource, setIsAddingReferralsSource] = useState(false);

  const runAddConsumerAsSource = async () => {
    setIsAddingReferralsSource(true);
    try {
      await onAddConsumerAsSource();
    } catch {
      // Survey patch mutation shows its own error snackbar on failure.
    } finally {
      setIsAddingReferralsSource(false);
    }
  };

  return (
    <div className="rounded-[8px] border border-solid border-neutral-300">
      <div className="px-[26px] py-[16px]">
        <div className="flex items-center gap-[8px]">
          <div className="text-[18px] leading-[20px] text-neutral-900 font-semibold">Referrals</div>
          {consumerSource && <NavCountBadge count={1} label="source" tone="neutral" />}
        </div>
        <div className="text-[16px] leading-[24px] text-neutral-600">
          If your business collects personal information about consumers from other consumers, such
          as a referral program where a customer gives you a friend’s name or email.
        </div>
        {consumerCollectionGroup && consumerSource ? (
          <div className="mt-[12px]">
            <VendorSourcingCard
              org={org}
              dataMap={dataMap}
              source={consumerSource}
              collectionGroupId={consumerCollectionGroup.id}
              receivedData={receivedData}
              onResetLocalChoice={onMarkConsumerNotASource}
            />
          </div>
        ) : (
          <>
            {offersFinancialIncentives && (
              <AlertCard
                variant="recommendation"
                description={
                  <>
                    In your <span className="font-medium">Business Survey</span> you said you offer
                    consumers a financial incentive, such as a loyalty or rewards program. If it
                    rewards customers for referring friends and you receive the friend’s name or
                    email, we recommend adding Referrals as a source.
                  </>
                }
              />
            )}
            <div className="mt-[12px] flex flex-wrap items-center gap-[12px]">
              <Button
                color="tertiary"
                size="xs"
                disabled={isAddingReferralsSource}
                onClick={runAddConsumerAsSource}
              >
                {isAddingReferralsSource ? "Adding…" : "Add Referrals as a Source"}
              </Button>
              <NoSourcesYetNote />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function NoSourcesYetNote() {
  return (
    <span className="text-[13px] leading-[20px]" style={{ color: "#726D83" }}>
      No sources added yet — skip this section if it doesn't apply to your business.
    </span>
  );
}

function VendorCategorySources({
  org,
  dataMap,
  collectionGroupIds,
  orgDataSources,
  receivedData,
  vendorCategories,
  sectionTitle,
  sectionDescription,
  addSourceButtonLabel,
  modalTitle,
  modalDescription,
  modalSearchPlaceholder,
  onLaunchAddSourcesModal,
  removeDataFlowSource,
  quickAddCandidates,
  onQuickAddSource,
}: {
  org: OrganizationDto;
  dataMap: DataMapDto;
  collectionGroupIds: string[];
  orgDataSources: OrganizationDataSourceDto[];
  receivedData: UseReceivedForCollectionGroups;
  vendorCategories: string[];
  sectionTitle: string;
  sectionDescription: string;
  addSourceButtonLabel: string;
  modalTitle?: string;
  modalDescription?: string;
  modalSearchPlaceholder?: string;
  onLaunchAddSourcesModal: (modalConfig: LaunchAddSourcesModalConfig) => void;
  removeDataFlowSource: (vendorId: string, afterSuccess?: () => void) => Promise<void>;
  /** Vendor List recipients the catalog also marks as sources and that aren't sources yet (any category). */
  quickAddCandidates: OrganizationVendor[];
  onQuickAddSource: (vendor: OrganizationVendor) => Promise<void>;
}) {
  const categoryFilter = useMemo(() => new Set(vendorCategories), [vendorCategories]);
  const matchingSources = useMemo(
    () => orgDataSources.filter((source) => source.category && categoryFilter.has(source.category)),
    [orgDataSources, categoryFilter],
  );
  const sectionQuickAddCandidates = useMemo(
    () => quickAddCandidates.filter((vendor) => categoryFilter.has(vendor.category)),
    [quickAddCandidates, categoryFilter],
  );
  const [quickAddingVendorId, setQuickAddingVendorId] = useState<string | null>(null);

  return (
    <div className="rounded-[8px] border border-solid border-neutral-300">
      <div className="px-[26px] py-[16px]">
        <div className="flex items-center gap-[8px]">
          <div className="text-[18px] leading-[20px] text-neutral-900 font-semibold">
            {sectionTitle}
          </div>
          {matchingSources.length > 0 && (
            <NavCountBadge
              count={matchingSources.length}
              label={matchingSources.length === 1 ? "source" : "sources"}
              tone="neutral"
            />
          )}
        </div>
        <div className="text-[16px] leading-[24px] text-neutral-600">{sectionDescription}</div>
        <div className="mt-[12px] flex flex-wrap items-center gap-[12px]">
          <Button
            color="tertiary"
            size="xs"
            onClick={() =>
              onLaunchAddSourcesModal({
                title: modalTitle,
                description: modalDescription ?? sectionDescription,
                searchPlaceholder: modalSearchPlaceholder,
              })
            }
          >
            {addSourceButtonLabel}
          </Button>
          {matchingSources.length === 0 && sectionQuickAddCandidates.length === 0 && (
            <NoSourcesYetNote />
          )}
        </div>
        {sectionQuickAddCandidates.length > 0 && (
          <div className="mt-[12px]">
            <div className="text-[13px] leading-[20px] text-neutral-600">
              Already in your Vendor List — add as a source:
            </div>
            <div className="mt-[6px] flex flex-wrap gap-[8px]">
              {sectionQuickAddCandidates.map((vendor) => (
                <button
                  key={vendor.vendorId}
                  type="button"
                  className="inline-flex items-center gap-[6px] rounded-full border border-solid border-neutral-300 bg-white px-[10px] py-[4px] text-[13px] font-medium text-neutral-700 cursor-pointer hover:border-primary-600 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={quickAddingVendorId !== null}
                  onClick={async () => {
                    setQuickAddingVendorId(vendor.vendorId);
                    try {
                      await onQuickAddSource(vendor);
                    } finally {
                      setQuickAddingVendorId(null);
                    }
                  }}
                >
                  {vendor.logoUrl && (
                    <img src={vendor.logoUrl} alt="" className="h-4 w-4 object-contain" />
                  )}
                  {vendor.name}
                  <PlusIcon className="h-4 w-4 text-primary-600" />
                </button>
              ))}
            </div>
          </div>
        )}
        <div className={clsx("space-y-[12px]", matchingSources.length > 0 && "mt-[12px]")}>
          {matchingSources.map((source) => (
            <div key={`${source.vendorId}-sources`}>
              <VendorSourcingCard
                org={org}
                dataMap={dataMap}
                source={source}
                collectionGroupId={collectionGroupIds}
                receivedData={receivedData}
                onRemoveSource={() => removeDataFlowSource(source.vendorId)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OtherSources({
  org,
  dataMap,
  collectionGroupIds,
  orgDataSources,
  receivedData,
  onLaunchAddSourcesModal,
  removeDataFlowSource,
  collectionContext,
  excludedCategories,
  excludedVendorKeys,
  sectionDescription,
}: {
  org: OrganizationDto;
  dataMap: DataMapDto;
  collectionGroupIds: string[];
  orgDataSources: OrganizationDataSourceDto[];
  receivedData: UseReceivedForCollectionGroups;
  onLaunchAddSourcesModal: (modalConfig: LaunchAddSourcesModalConfig) => void;
  removeDataFlowSource: (vendorId: string, afterSuccess?: () => void) => Promise<void>;
  collectionContext: CollectionContext;
  excludedCategories: readonly string[];
  excludedVendorKeys: readonly string[];
  sectionDescription: string;
}) {
  const otherSources = useMemo(
    () =>
      orgDataSources.filter((source) =>
        isOtherDataSource(source, collectionContext, excludedCategories, excludedVendorKeys),
      ),
    [orgDataSources, collectionContext, excludedCategories, excludedVendorKeys],
  );

  return (
    <div className="rounded-[8px] border border-solid border-neutral-300">
      <div className="px-[26px] py-[16px]">
        <div className="flex items-center gap-[8px]">
          <div className="text-[18px] leading-[20px] text-neutral-900 font-semibold">
            Other Sources
          </div>
          {otherSources.length > 0 && (
            <NavCountBadge
              count={otherSources.length}
              label={otherSources.length === 1 ? "source" : "sources"}
              tone="neutral"
            />
          )}
        </div>
        <div className="text-[16px] leading-[24px] text-neutral-600">{sectionDescription}</div>
        <div className="mt-[12px] flex flex-wrap items-center gap-[12px]">
          <Button
            color="tertiary"
            size="xs"
            onClick={() =>
              onLaunchAddSourcesModal({
                title: "Add Other Source",
                description: sectionDescription,
                searchPlaceholder: "Search over 2,500 vendors or add a custom source",
              })
            }
          >
            Add Other Source
          </Button>
          {otherSources.length === 0 && <NoSourcesYetNote />}
        </div>
        <div className={clsx("space-y-[12px]", otherSources.length > 0 && "mt-[12px]")}>
          {otherSources.map((source) => (
            <div key={`${source.vendorId}-other-sources`}>
              <VendorSourcingCard
                org={org}
                dataMap={dataMap}
                source={source}
                collectionGroupId={collectionGroupIds}
                receivedData={receivedData}
                onRemoveSource={() => removeDataFlowSource(source.vendorId)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export type DataFlowsSourceCategoryConfig = {
  vendorCategories: string[];
  sectionTitle: string;
  sectionDescription: string;
  addSourceButtonLabel: string;
  modalTitle?: string;
  modalDescription?: string;
  modalSearchPlaceholder?: string;
};

export type DataFlowsSourcesPageConfig = {
  collectionContext: CollectionContext;
  surveyName: string;
  primarySource: {
    vendorKey: string;
    surveySlug: string;
    surveyQuestion: string;
    sourceName: string;
    sectionTitle: string;
    sectionDescription: string;
    recommendation?: string;
    preselectCollectedPics: boolean;
  };
  categorySources: DataFlowsSourceCategoryConfig[];
  otherSources: {
    excludedCategories: readonly string[];
    excludedVendorKeys: readonly string[];
    sectionDescription: string;
  };
  showReferrals: boolean;
  isHr: boolean;
  includeAllContextsInExistingSources: boolean;
  addKnownSourcesWithExplicitContext: boolean;
};

export type DataFlowsSourcesFooterState = {
  isComplete: boolean;
  isCompleteTooltip: string | null;
};

const CONSUMER_DATA_FLOWS_SOURCES_CONFIG: DataFlowsSourcesPageConfig = {
  collectionContext: "CONSUMER",
  surveyName: CONSUMER_SOURCES_SURVEY_NAME,
  primarySource: {
    vendorKey: SOURCE_KEY_CONSUMER_DIRECTLY,
    surveySlug: CONSUMER_DIRECTLY_SLUG,
    surveyQuestion: CONSUMER_DIRECTLY_QUESTION,
    sourceName: "Consumer",
    sectionTitle: "Consumers",
    sectionDescription:
      "The consumer, or any other primary collection group, is usually the primary data source for a business.",
    recommendation:
      "We recommend adding “Consumer” as a source. It is very uncommon that a business does not collect data from their consumers either directly or indirectly.",
    preselectCollectedPics: true,
  },
  categorySources: [
    {
      vendorCategories: [VENDOR_CATEGORY_RETAIL_PARTNER],
      sectionTitle: "Retail Partners",
      sectionDescription:
        "If your business offers products through a third-party selling platform, such as Wal-Mart or Amazon Marketplace, and receives personal information (order information, postal address, etc.) about customers via that platform.",
      addSourceButtonLabel: "Add Retail Partner Source",
      modalTitle: "Add Retail Partner Source",
    },
    {
      vendorCategories: [VENDOR_CATEGORY_DATA_BROKER],
      sectionTitle: "Data Brokers",
      sectionDescription:
        "If your business sources personal data from data brokers. Data brokers are third parties that aggregate personal data about people they do not have a direct relationship with, and sell it to businesses. ZoomInfo is an example of a data broker.",
      addSourceButtonLabel: "Add Data Broker Source",
      modalTitle: "Add Data Broker Source",
    },
    {
      vendorCategories: [VENDOR_CATEGORY_AD_NETWORK],
      sectionTitle: "Ad Networks",
      sectionDescription:
        "Only add ad networks sources from which you collect data that can be linked to individual consumers. For example, if you run any Leads Campaigns on Facebook Ads or Lead Generation Conversion campaigns on LinkedIn Ads.",
      addSourceButtonLabel: "Add Ad Network Source",
      modalTitle: "Add Ad Network Source",
    },
    {
      vendorCategories: [VENDOR_CATEGORY_DATA_ANALYTICS_PROVIDER],
      sectionTitle: "Data Analytics Provider",
      sectionDescription:
        "If you collect data from analytics providers that can be linked to individual consumers, such as Google Analytics.",
      addSourceButtonLabel: "Add Data Analytics Source",
      modalTitle: "Add Data Analytics Source",
    },
  ],
  otherSources: {
    excludedCategories: OTHER_SOURCE_EXCLUDED_CATEGORIES,
    excludedVendorKeys: OTHER_SOURCE_EXCLUDED_VENDOR_KEYS,
    sectionDescription:
      "Add any other sources that your business receives personal information from.",
  },
  showReferrals: true,
  isHr: false,
  includeAllContextsInExistingSources: true,
  addKnownSourcesWithExplicitContext: false,
};

/**
 * Shown when the org has no non-employment collection group at all — there is nothing to source
 * data into yet, so the page explains where to start instead of spinning on <Loading />.
 */
function DataFlowsSourcesEmptyState({ hasError }: { hasError: boolean }) {
  const history = useHistory();

  return (
    <StandardPageWrapper fullWidth>
      <div className="tw-boundary">
        <div style={{ marginTop: -40, marginBottom: 6 }}>
          <Breadcrumbs
            hideHomeIcon={true}
            title="Data Flows"
            source="Data Map"
            sourceRoute={Routes.dataMap.dashboard}
          />
        </div>
        <div className="mt-[24px]" style={{ maxWidth: DATA_FLOWS_CONTENT_MAX_WIDTH }}>
          <AlertCard
            variant={hasError ? "error" : "info"}
            description={
              hasError
                ? "We couldn't load your collection groups. Refresh the page to try again."
                : "You don't have any consumer collection groups yet. Create one on the Data Flows page, then come back here to record where each category of data comes from."
            }
          />
          <div className="mt-[16px]">
            <Button
              color="primary"
              onClick={() => history.push(Routes.dataMap.dataFlows.dashboard)}
            >
              Go to Data Flows
            </Button>
          </div>
        </div>
      </div>
    </StandardPageWrapper>
  );
}

export function DataFlowsSourcesPage() {
  const history = useHistory();
  const orgId = usePrimaryOrganizationId();
  const [consumerCollectionGroup, consumerCollectionGroupRequest] =
    useConsumerCollectionGroup(orgId);
  // Orgs whose autocreated Consumers group was renamed away or never created still have
  // data flows to source — fall back to every non-employment group (the completeness check
  // and the backend both reason over all of them). Previously this page hung on <Loading/>.
  const [nonEmploymentGroups, nonEmploymentGroupsRequest] = useNonEmploymentCollectionGroups(
    orgId,
    /* onlyUseSettled = */ true,
  );
  const [footerState, setFooterState] = useState<DataFlowsSourcesFooterState>({
    isComplete: false,
    isCompleteTooltip: null,
  });
  const invalidateOnboardingProgress = useInvalidateOnboardingProgress(orgId);
  // Consumer-only page, so the HR branch of the gate never applies. Sources has no per-step
  // "incomplete" escape hatch to pass — the footer's own isComplete already drives its CTA.
  const showOnboardingFooter = useDataFlowsOnboardingFooterGate(/* isHr = */ false);
  // Finishing Sources is what completes Data Flows, so that gate alone drops this footer the moment
  // it's needed. Keep the way forward until Vendor Documents, the next data map step, is done.
  const [primaryOrg] = usePrimaryOrganization();
  const [onboardingProgress] = useOnboardingProgress(orgId);
  const isBeforeVendorDocumentation =
    primaryOrg?.finishedOnboardingLatch === false &&
    onboardingProgress != null &&
    !onboardingProgress.dataMapOnboardingProgress.isVendorDocumentationComplete;

  // Every non-employment group, not just the autocreated Consumers one: the dashboard's
  // narration and the backend's isSourcesPageComplete both reason over all of them, and scoping
  // this page more narrowly made a second group's source mappings invisible here while still
  // counting towards completion elsewhere.
  const collectionGroups =
    nonEmploymentGroups && nonEmploymentGroups.length > 0
      ? nonEmploymentGroups
      : consumerCollectionGroup
      ? [consumerCollectionGroup]
      : [];
  // Sources is the flow's last stop — while any group's three steps are unfinished the dashboard
  // disables this page's tab, and the onboarding footer stays hidden here so a direct navigation
  // can't "complete" Sources early (POL-7818).
  const stepsCompletion = useDataFlowCollectionGroupsCompletion({
    config: CONSUMER_DATA_FLOWS_DASHBOARD_CONFIG,
    groups: collectionGroups.length > 0 ? collectionGroups : undefined,
  });
  const allGroupsComplete =
    stepsCompletion.isReady && stepsCompletion.allComplete && stepsCompletion.purposesComplete;
  const requestFailed = nonEmploymentGroupsRequest.error && consumerCollectionGroupRequest.error;
  if (collectionGroups.length === 0) {
    // Both hooks answer with an empty value rather than an error when the org simply has no
    // groups, so this has to distinguish "still fetching" from "nothing to show" — otherwise a
    // group-less org sits on <Loading /> forever.
    if (nonEmploymentGroupsRequest.running || consumerCollectionGroupRequest.running) {
      return <Loading />;
    }
    return <DataFlowsSourcesEmptyState hasError={requestFailed} />;
  }

  const tabsConfig = [
    {
      name: "Collection Groups",
      tabKey: "collection-groups",
      route: Routes.dataMap.dataFlows.dashboard,
      current: false,
    },
    {
      name: "Purposes",
      tabKey: "purposes",
      route: Routes.dataMap.dataFlows.purposes,
      current: false,
      showIncompleteIndicator: stepsCompletion.isReady && !stepsCompletion.purposesComplete,
    },
    {
      name: "Sources",
      tabKey: "sources",
      route: Routes.dataMap.dataFlows.sources,
      current: true,
      showIncompleteIndicator: !footerState.isComplete,
    },
  ];

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        (showOnboardingFooter || isBeforeVendorDocumentation) && allGroupsComplete ? (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Sources"
                nextOnClick={() => {
                  invalidateOnboardingProgress();
                  history.push(Routes.dataMap.vendorDocumentation);
                }}
                nextDisabled={!footerState.isComplete}
                nextDisabledTooltip={footerState.isCompleteTooltip}
              />
            }
          />
        ) : null
      }
    >
      <div className="tw-boundary">
        <DataFlowsStickyHeader
          breadcrumb={{
            title: "Data Flows",
            source: "Data Map",
            sourceRoute: Routes.dataMap.dashboard,
          }}
          tabs={tabsConfig}
        />

        <DataFlowsSourcesPageContent
          config={CONSUMER_DATA_FLOWS_SOURCES_CONFIG}
          collectionGroups={collectionGroups}
          onFooterStateChange={setFooterState}
        />
      </div>
    </StandardPageWrapper>
  );
}

export function DataFlowsSourcesPageContent({
  config,
  collectionGroups,
  onFooterStateChange,
}: {
  config: DataFlowsSourcesPageConfig;
  collectionGroups: CollectionGroupDetailsDto[];
  onFooterStateChange?: (footerState: DataFlowsSourcesFooterState) => void;
}) {
  const history = useHistory();
  const [org, , ,] = usePrimaryOrganization();
  const orgId = usePrimaryOrganizationId();
  const showSnackbar = useSnackBar();
  const queryClient = useQueryClient();

  const {
    answers: sourcesSurveyAnswers,
    setAnswer: setSourcesSurveyAnswer,
    request: sourcesSurveyRequest,
  } = useSurvey(orgId, config.surveyName);
  const { answers: incentivesSurveyAnswers } = useSurvey(
    orgId,
    CONSUMER_INCENTIVES_SURVEY_NAME,
    undefined,
    /* notReady = */ !config.showReferrals,
  );

  const {
    data: dataMap,
    refresh: refreshDataMap,
    mutators: { patch: patchDataMap },
  } = useDataMap(orgId);

  const [orgDataSources, , refreshOrgDataSources] = useOrgDataSources(orgId);

  // Quick-add suggestions: vendors already in the org's Vendor List for this context that the
  // catalog also marks as data sources (e.g. Meta Ads, Google Analytics). Filtered against
  // existing sources below, and by category inside each section.
  const recipientContext = useMemo(() => [config.collectionContext], [config.collectionContext]);
  const [orgRecipients] = useOrganizationVendors(
    orgId,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ recipientContext,
    /* onlyUseSettled = */ true,
  );
  const [dataSourceCatalog] = useDataSourceCatalog(orgId);
  const dismissUndoToastRef = useRef<(() => void) | null>(null);
  useEffect(() => () => dismissUndoToastRef.current?.(), []);

  const [addSourcesModal, setAddSourcesModal] = useState<LaunchAddSourcesModalConfig | null>(null);
  const [recipientPrompts, setRecipientPrompts] = useState<SourceRecipientPrompt[]>([]);

  const collectionGroupIds = useMemo(
    () => collectionGroups.map((group) => group.id),
    [collectionGroups],
  );

  const contextualSources = useMemo(
    () =>
      orgDataSources?.filter((source) =>
        source.collectionContext.includes(config.collectionContext),
      ) ?? [],
    [orgDataSources, config.collectionContext],
  );

  const existingSourceIds = useMemo(
    () =>
      new Set(
        (config.includeAllContextsInExistingSources ? orgDataSources : contextualSources)?.map(
          (source) => source.vendorId,
        ) ?? [],
      ),
    [orgDataSources, contextualSources, config.includeAllContextsInExistingSources],
  );

  const existingSourceNames = useMemo(
    () =>
      new Set(
        (config.includeAllContextsInExistingSources ? orgDataSources : contextualSources)?.map(
          (source) => source.name.trim().toLowerCase(),
        ) ?? [],
      ),
    [orgDataSources, contextualSources, config.includeAllContextsInExistingSources],
  );

  const quickAddCandidates = useMemo(() => {
    const sourceCapableVendorIds = new Set(dataSourceCatalog.map((vendor) => vendor.id));
    return (orgRecipients ?? []).filter(
      (vendor) =>
        sourceCapableVendorIds.has(vendor.vendorId) && !existingSourceIds.has(vendor.vendorId),
    );
  }, [orgRecipients, dataSourceCatalog, existingSourceIds]);

  const handleAddSourcesDone = async (selectedSources: SelectedSource[]) => {
    const hasCustomSources = selectedSources.some((s) => s.custom != null);
    try {
      const addedSources = await Promise.all(
        selectedSources.map((s) => {
          if (s.custom) {
            return Api.orgDataSource.addOrgDataSource(orgId, {
              collectionContext: [config.collectionContext],
              custom: s.custom,
            });
          }
          if (s.id) {
            return Api.orgDataSource.addOrgDataSource(orgId, {
              vendorId: s.id,
              ...(config.addKnownSourcesWithExplicitContext
                ? { collectionContext: [config.collectionContext] }
                : {}),
            });
          }
          return Promise.resolve(null);
        }),
      );
      if (hasCustomSources) {
        queryClient.invalidateQueries(["vendorCatalog", orgId]);
      }
      // Read every recipient fresh, whatever its context: one that is a recipient only in the other
      // context (or that the cached list hasn't loaded) must keep its settings when it gains this
      // context, so it can't be mistaken for a new vendor.
      const allRecipients = await Api.organizationVendor.getVendors(
        orgId,
        /* excludeCategories = */ undefined,
        /* includeDataStorageLocations = */ true,
      );
      const recipientsById = new Map(allRecipients.map((r) => [r.vendorId, r]));
      const newSourcesNotRecipients = addedSources.filter(
        (source): source is OrganizationDataSourceDto => {
          const recipient = source && recipientsById.get(source.vendorId);
          return (
            source != null &&
            !(
              recipient &&
              shouldShowInInventoryContext(
                recipient.collectionContext,
                config.collectionContext,
                /* includeEmptyContext = */ false,
              )
            )
          );
        },
      );
      // HR asks about every new source, ad networks included, rather than adding employee
      // recipients on its own.
      const adNetworkRecipients = config.isHr
        ? new Map<string, OrganizationDataRecipientDto>()
        : await addAdNetworkSourcesAsRecipients(newSourcesNotRecipients, recipientsById);
      await Promise.all([refreshOrgDataSources(), refreshDataMap()]);
      setAddSourcesModal(null);
      // Ask about each new source's recipient role, one at a time.
      setRecipientPrompts(
        newSourcesNotRecipients.map((source) => ({
          source,
          recipient: adNetworkRecipients.get(source.vendorId),
          existingRecipient: recipientsById.get(source.vendorId),
        })),
      );
    } catch {
      showSnackbar("Could not add source(s). Please try again.", { variant: "error" });
    }
  };

  // An ad network you collect data from (lead ads, audience syncs) is one you also send data to
  // through its pixel or SDK, so a new consumer ad-network source joins the Vendor List as a
  // recipient. Takes sources not yet in the list; the backend merges contexts. Returns the new
  // recipients by vendor id.
  const addAdNetworkSourcesAsRecipients = async (
    sourcesNotRecipients: OrganizationDataSourceDto[],
    recipientsById: Map<string, OrganizationDataRecipientDto>,
  ): Promise<Map<string, OrganizationDataRecipientDto>> => {
    const adNetworks = sourcesNotRecipients.filter(
      (source) => source.category === VENDOR_CATEGORY_AD_NETWORK,
    );
    const added = new Map<string, OrganizationDataRecipientDto>();
    if (adNetworks.length === 0) {
      return added;
    }
    const results = await Promise.allSettled(
      adNetworks.map((source) =>
        Api.organizationVendor.addOrUpdateVendor(
          orgId,
          source.vendorId,
          addRecipientContextSettings(
            [config.collectionContext],
            recipientsById.get(source.vendorId),
          ),
        ),
      ),
    );
    void queryClient.invalidateQueries({ queryKey: ["organizationVendors", orgId] });
    results.forEach((result, idx) => {
      if (result.status === "fulfilled") {
        added.set(adNetworks[idx].vendorId, result.value);
      }
    });
    return added;
  };

  const handleRemoveDataFlowSource = async (
    vendorId: string,
    afterSuccess?: () => void,
  ): Promise<void> => {
    // Snapshot what removal destroys so the toast's Undo can put it back: the source itself (the
    // backend deletes it, and may soft-delete a custom vendor, once its last collection context
    // goes) and the categories sourced from it in this page's collection groups.
    const removedSource = orgDataSources?.find((source) => source.vendorId === vendorId);
    const removedReceived = dataMap.received
      .filter((r) => r.vendorId === vendorId && collectionGroupIds.includes(r.groupId))
      .map((r) => ({ groupId: r.groupId, picId: r.picId }));

    try {
      await Api.orgDataSource.removeOrgDataSource(orgId, vendorId, [config.collectionContext]);
      await Promise.all([refreshOrgDataSources(), refreshDataMap()]);
      afterSuccess?.();
    } catch {
      showSnackbar("Could not remove source. Please try again.", { variant: "error" });
      return;
    }

    if (!removedSource) {
      return;
    }

    // Restores by vendor id. Only a custom vendor the backend deleted with its last source context
    // (it keeps one that is still a recipient, or that request history refers to) answers 404, and
    // only then is it re-created.
    const restoreSource = async () => {
      try {
        return await Api.orgDataSource.addOrgDataSource(orgId, {
          collectionContext: [config.collectionContext],
          vendorId,
        });
      } catch (e) {
        if (!removedSource.isCustom || !axios.isAxiosError(e) || e.response?.status !== 404) {
          throw e;
        }
        const recreated = await Api.orgDataSource.addOrgDataSource(orgId, {
          collectionContext: [config.collectionContext],
          custom: {
            name: removedSource.name,
            category: removedSource.category ?? "",
            url: removedSource.url,
            contactEmail: removedSource.email,
          },
        });
        queryClient.invalidateQueries(["vendorCatalog", orgId]);
        return recreated;
      }
    };

    const undoRemoval = async () => {
      dismissUndoToast();
      try {
        const restored = await restoreSource();
        if (removedReceived.length > 0) {
          const operations: DataMapOperation[] = removedReceived.map((r) => ({
            target: "RECEIVED",
            action: "ADD",
            groupId: r.groupId,
            vendorId: restored.vendorId,
            picId: r.picId,
          }));
          await patchDataMap.mutateAsync({ operations });
        }
        await Promise.all([refreshOrgDataSources(), refreshDataMap()]);
        showSnackbar(`${removedSource.name} was restored as a source.`, { variant: "success" });
      } catch {
        showSnackbar("Could not restore source. Please try again.", { variant: "error" });
      }
    };

    // Stays until Undo or close, since the removal can only be undone from here. One at a time: a
    // new removal replaces the previous toast, and leaving the page dismisses it (the snackbar list
    // outlives the page, and Undo acts on this page's context).
    dismissUndoToastRef.current?.();
    const dismissUndoToast = showSnackbar(`${removedSource.name} was removed as a source.`, {
      variant: "info",
      persist: true,
      action: (
        <button
          type="button"
          className="font-medium underline cursor-pointer bg-transparent border-0 p-0 whitespace-nowrap"
          onClick={undoRemoval}
        >
          Undo
        </button>
      ),
    });
    dismissUndoToastRef.current = dismissUndoToast;
  };

  const receivedData = useReceivedForCollectionGroups(orgId, collectionGroups, [
    config.collectionContext,
  ]);

  const [isComplete, isCompleteTooltip] = useMemo<[boolean, string | null]>(() => {
    if (!dataMap || isEmptyDataMap(dataMap) || collectionGroupIds.length === 0) {
      return [false, null];
    }

    return isSourcesPageComplete(
      orgDataSources,
      dataMap,
      collectionGroupIds,
      config.collectionContext,
      { [config.primarySource.vendorKey]: config.primarySource.sourceName },
    );
  }, [orgDataSources, dataMap, collectionGroupIds, config]);

  useEffect(() => {
    onFooterStateChange?.({ isComplete, isCompleteTooltip });
  }, [isComplete, isCompleteTooltip, onFooterStateChange]);

  if (
    !org ||
    collectionGroups.length === 0 ||
    !receivedData ||
    !receivedData.requests.sourcesRequest.success ||
    !sourcesSurveyRequest.success ||
    !dataMap ||
    isEmptyDataMap(dataMap)
  ) {
    return <Loading />;
  }

  // Sources describe where each collected category comes from — before Step 1 there is
  // nothing to source, and every section renders as an unfollowable dead end (DES-30).
  const hasAnyCollectedData = dataMap.collection.some((c) =>
    collectionGroupIds.includes(c.groupId),
  );
  if (!config.isHr && !hasAnyCollectedData) {
    return (
      <div
        className="bg-white border border-solid border-neutral-200 rounded-xl shadow-[0px_1px_2px_0px_#1018280D]"
        style={{ width: 560, maxWidth: "100%", margin: "120px auto 0", padding: 32 }}
      >
        <div
          className="flex items-center justify-center rounded-full"
          style={{ width: 48, height: 48, backgroundColor: "#F3F0FE" }}
        >
          <FingerPrintIcon className="h-6 w-6" style={{ color: "#3C1DA5" }} />
        </div>
        <div className="mt-[16px] text-[18px] font-semibold text-neutral-900">
          Choose what data you collect first
        </div>
        <div className="mt-[4px] text-[14px] leading-[21px] text-neutral-700">
          Sources describe where each data category comes from — so Step 1 of your collection groups
          comes first.
        </div>
        <div className="mt-[20px]">
          <Button color="primary" onClick={() => history.push(Routes.dataMap.dataFlows.dashboard)}>
            Go to Collection Groups
          </Button>
        </div>
      </div>
    );
  }

  const primarySource = receivedData.sources.find(
    (source) => source.vendorKey === config.primarySource.vendorKey,
  );
  const referralSource = receivedData?.sources?.find(
    (s) => s.vendorKey === SOURCE_KEY_OTHER_INDIVIDUALS,
  );

  return (
    <>
      <div className="mt-[24px]" style={{ maxWidth: DATA_FLOWS_CONTENT_MAX_WIDTH }}>
        <div>
          <SourcesExplanatoryCard isHr={config.isHr} />
        </div>

        <div className="mt-[24px]">
          <CollectionGroupSources
            org={org}
            dataMap={dataMap}
            collectionGroupIds={collectionGroupIds}
            source={primarySource}
            receivedData={receivedData}
            surveyAnswer={sourcesSurveyAnswers[config.primarySource.surveySlug]}
            sourceName={config.primarySource.sourceName}
            sectionTitle={config.primarySource.sectionTitle}
            sectionDescription={config.primarySource.sectionDescription}
            recommendation={config.primarySource.recommendation}
            showSelectAll={!config.isHr}
            onAddAsSource={async () => {
              await setSourcesSurveyAnswer(
                config.primarySource.surveySlug,
                "true",
                config.primarySource.surveyQuestion,
              );
              try {
                const refreshedSources = await Api.orgDataSource.getOrgDataSources(orgId);
                const refreshedPrimarySource = refreshedSources.find(
                  (source) =>
                    source.vendorKey === config.primarySource.vendorKey &&
                    source.collectionContext.includes(config.collectionContext),
                );
                const collectedPicIds = receivedData.collectedPIC.map((pic) => pic.id);

                if (
                  config.primarySource.preselectCollectedPics &&
                  refreshedPrimarySource &&
                  collectedPicIds.length > 0
                ) {
                  // POL-7510: Preselect all collected PICs for a newly added Consumer source.
                  await patchDataMap.mutateAsync({
                    operations: collectionGroupIds.flatMap((groupId) =>
                      collectedPicIds
                        .filter((picId) =>
                          dataMap.collection.some(
                            (collection) =>
                              collection.groupId === groupId && collection.picId === picId,
                          ),
                        )
                        .map((picId) => ({
                          target: "RECEIVED" as const,
                          action: "ADD" as const,
                          groupId,
                          vendorId: refreshedPrimarySource.vendorId,
                          picId,
                        })),
                    ),
                  });
                }
              } finally {
                await Promise.all([refreshOrgDataSources(), refreshDataMap()]);
              }
            }}
            onMarkNotASource={async () => {
              await setSourcesSurveyAnswer(
                config.primarySource.surveySlug,
                "false",
                config.primarySource.surveyQuestion,
              );
              await Promise.all([refreshOrgDataSources(), refreshDataMap()]);
            }}
          />
        </div>

        {config.showReferrals && (
          <div className="mt-[24px]">
            <ReferralsSource
              org={org}
              dataMap={dataMap}
              consumerCollectionGroup={collectionGroups[0]}
              consumerSource={referralSource}
              receivedData={receivedData}
              offersFinancialIncentives={
                incentivesSurveyAnswers[OFFERS_CONSUMER_INCENTIVES_SLUG] === "true"
              }
              onAddConsumerAsSource={async () => {
                await setSourcesSurveyAnswer(
                  OTHER_CONSUMERS_SLUG,
                  "true",
                  OTHER_CONSUMERS_QUESTION,
                );
                await Promise.all([refreshOrgDataSources(), refreshDataMap()]);
              }}
              onMarkConsumerNotASource={async () => {
                await setSourcesSurveyAnswer(
                  OTHER_CONSUMERS_SLUG,
                  "false",
                  OTHER_CONSUMERS_QUESTION,
                );
                await Promise.all([refreshOrgDataSources(), refreshDataMap()]);
              }}
            />
          </div>
        )}

        {config.categorySources.map((categorySource) => (
          <div className="mt-[24px]" key={categorySource.sectionTitle}>
            <VendorCategorySources
              org={org}
              dataMap={dataMap}
              collectionGroupIds={collectionGroupIds}
              orgDataSources={
                config.includeAllContextsInExistingSources ? orgDataSources : contextualSources
              }
              receivedData={receivedData}
              {...categorySource}
              onLaunchAddSourcesModal={setAddSourcesModal}
              removeDataFlowSource={handleRemoveDataFlowSource}
              quickAddCandidates={quickAddCandidates}
              onQuickAddSource={(vendor) =>
                handleAddSourcesDone([
                  { id: vendor.vendorId, name: vendor.name, logoUrl: vendor.logoUrl },
                ])
              }
            />
          </div>
        ))}

        <div className="mt-[24px]">
          <OtherSources
            org={org}
            dataMap={dataMap}
            collectionGroupIds={collectionGroupIds}
            orgDataSources={contextualSources}
            receivedData={receivedData}
            onLaunchAddSourcesModal={setAddSourcesModal}
            removeDataFlowSource={handleRemoveDataFlowSource}
            collectionContext={config.collectionContext}
            excludedCategories={config.otherSources.excludedCategories}
            excludedVendorKeys={config.otherSources.excludedVendorKeys}
            sectionDescription={config.otherSources.sectionDescription}
          />
        </div>
      </div>

      {addSourcesModal && (
        <AddSourceModal
          organizationId={orgId}
          existingSourceIds={existingSourceIds}
          existingSourceNames={existingSourceNames}
          otherExcludedVendorKeys={config.otherSources.excludedVendorKeys}
          title={addSourcesModal.title}
          description={addSourcesModal.description}
          searchPlaceholder={addSourcesModal.searchPlaceholder}
          onClose={() => setAddSourcesModal(null)}
          onDone={handleAddSourcesDone}
        />
      )}

      {recipientPrompts.length > 0 && (
        <SourceRecipientPromptModal
          key={recipientPrompts[0].source.vendorId}
          org={org}
          prompt={recipientPrompts[0]}
          collectionContext={config.collectionContext}
          onAddedAsRecipient={() => {
            void queryClient.invalidateQueries({ queryKey: ["organizationVendors", orgId] });
            refreshDataMap();
          }}
          onClose={() => setRecipientPrompts((prev) => prev.slice(1))}
        />
      )}
    </>
  );
}
